import { CircleAlert, CircleCheck, Info, type LucideIcon, TriangleAlert } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "../lib/cn";
import { Icon } from "./icon";

type NoteTone = "progress" | "attention" | "problem" | "done";

const NOTE: Record<NoteTone, { readonly box: string; readonly icon: LucideIcon; readonly mark: string }> = {
  progress: { box: "bg-tone-progress-bg text-tone-progress-fg", icon: Info, mark: "text-tone-progress-dot" },
  attention: {
    box: "bg-tone-attention-bg text-tone-attention-fg",
    icon: TriangleAlert,
    mark: "text-tone-attention-dot",
  },
  problem: {
    box: "bg-tone-problem-bg text-tone-problem-fg",
    icon: CircleAlert,
    mark: "text-tone-problem-dot",
  },
  done: { box: "bg-tone-done-bg text-tone-done-fg", icon: CircleCheck, mark: "text-tone-done-dot" },
};

/**
 * An inline alert (docs/design/meridian.md): the tone's fill and icon, 13 / 20 text, an optional bold `title`, and at
 * most one action. "No escrow protection" and "the account name differs" are `attention` notes.
 */
export function Note({
  tone = "progress",
  title,
  children,
  action,
  role = "note",
  className,
}: {
  tone?: NoteTone;
  title?: string;
  children?: ReactNode;
  action?: ReactNode;
  role?: "note" | "alert" | "status";
  className?: string;
}) {
  const note = NOTE[tone];
  return (
    <div
      role={role}
      className={cn("flex gap-2.5 rounded-(--radius-control) p-3 text-small", note.box, className)}
    >
      <Icon icon={note.icon} className={cn("mt-0.5 shrink-0", note.mark)} />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        {title !== undefined && <p className="font-semibold">{title}</p>}
        {children !== undefined && <div>{children}</div>}
      </div>
      {action !== undefined && <div className="shrink-0 self-center">{action}</div>}
    </div>
  );
}
